import { BrowserMultiFormatReader } from "@zxing/library";
import clsx from "clsx";
import { FC, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Camera } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Modal } from "@/components/Interface/Modal/Modal";
import { Notification } from "@/components/Interface/Notification/Notification";

interface CameraScannerProps {
  /** Renders the trigger as a bare icon, for use inside an input field. */
  iconOnly?: boolean;
  onScan: (value: string) => void;
  disabled?: boolean;
}

export const CameraScanner: FC<CameraScannerProps> = ({
  iconOnly = false,
  onScan,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch {
      setError(t("Onboarding.sections.sim-verification.scanner.cameraError"));
    }
  }, [t]);

  const handleOpen = () => {
    setIsOpen(true);
    setError(null);
    setTimeout(() => startCamera(), 100);
  };

  const handleClose = () => {
    stopCamera();
    setIsOpen(false);
    setIsScanning(false);
    setError(null);
  };

  const performScan = async () => {
    if (!videoRef.current || isScanning) {
      return;
    }

    setIsScanning(true);
    setError(null);

    try {
      const codeReader = new BrowserMultiFormatReader();
      const canvas = document.createElement("canvas");
      const video = videoRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        return;
      }

      ctx.drawImage(video, 0, 0);
      const imageDataUrl = canvas.toDataURL("image/png");
      const result = await codeReader.decodeFromImageUrl(imageDataUrl);

      if (result?.getText()) {
        const barcodeText = result.getText();
        const numbers = barcodeText.replace(/\D/g, "");

        if (numbers.length >= 19) {
          const iccid = numbers.substring(0, 20);
          onScan(iccid);
          handleClose();

          return;
        }
      }

      setError(t("Onboarding.sections.sim-verification.scanner.noBarcode"));
    } catch {
      setError(t("Onboarding.sections.sim-verification.scanner.noBarcode"));
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <>
      {iconOnly ? (
        <button
          className="flex size-6 items-center justify-center text-foreground-primary disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          aria-label={t("Onboarding.sections.sim-verification.scanner.button")}
          disabled={disabled}
          onClick={handleOpen}
        >
          <Camera className="size-6" />
        </button>
      ) : (
        <Button
          className="mt-2"
          type="button"
          variant="secondary"
          fullWidth
          disabled={disabled}
          icon={<ScanIcon />}
          onClick={handleOpen}
        >
          {t("Onboarding.sections.sim-verification.scanner.button")}
        </Button>
      )}

      {isOpen && (
        <Modal
          title={t("Onboarding.sections.sim-verification.scanner.title")}
          onClose={handleClose}
          hasScroll={false}
          variant="dark"
        >
          <div className="flex flex-col gap-4">
            <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-black">
              <video
                className="h-full w-full object-cover"
                ref={videoRef}
                autoPlay
                playsInline
                muted
              />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div
                  className={clsx(
                    "block h-[25%] w-[85%] rounded-lg border-2 border-dashed border-border-primary-inverse opacity-80",
                    isScanning && "animate-pulse",
                  )}
                />
              </div>
            </div>

            <p className="text-center text-body-sm text-foreground-secondary-inverse">
              {t("Onboarding.sections.sim-verification.scanner.hint")}
            </p>

            {error && (
              <Notification
                tone="error"
                inverted
                description={error}
                onClose={() => setError(null)}
              />
            )}

            <div className="flex justify-center">
              <Button
                variant="primary"
                inverted
                fullWidth
                loading={isScanning}
                onClick={() => void performScan()}
              >
                {isScanning
                  ? t("Onboarding.sections.sim-verification.scanner.scanning")
                  : t("Onboarding.sections.sim-verification.scanner.scan")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

const ScanIcon: FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7V5a2 2 0 0 1 2-2h2" />
    <path d="M17 3h2a2 2 0 0 1 2 2v2" />
    <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
    <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
    <line x1="4" y1="12" x2="20" y2="12" />
  </svg>
);
